import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function GuestResponsibilitiesPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Guest Responsibilities & Conduct
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.guest_responsibilities}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-600 mb-4">
              As a Guest on Southwest Florida Vacation Rentals, you agree to these responsibilities 
              and conduct standards. Respecting these guidelines ensures a positive experience for 
              you, Hosts, and the community.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Booking Accuracy
            </h2>
            <p className="text-slate-600 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide accurate information when booking</li>
              <li>Only book for the number of guests you've indicated</li>
              <li>Use the property only for the stated purpose (vacation rental)</li>
              <li>Not misrepresent your identity or intentions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. House Rules
            </h2>
            <p className="text-slate-600 mb-4">
              Each property has specific house rules. By booking, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Read and understand the house rules before booking</li>
              <li>Comply with all house rules during your stay</li>
              <li>Respect quiet hours and noise restrictions</li>
              <li>Follow parking and guest policies</li>
              <li>Adhere to smoking/no-smoking policies</li>
              <li>Respect pet policies (if applicable)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Property Care
            </h2>
            <p className="text-slate-600 mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Treating the property with care and respect</li>
              <li>Reporting any damages or issues immediately to the Host</li>
              <li>Not making alterations to the property</li>
              <li>Using amenities and appliances as intended</li>
              <li>Securing the property when leaving (locking doors, closing windows)</li>
              <li>Leaving the property in a reasonable condition at checkout</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Damage Responsibility
            </h2>
            <p className="text-slate-600 mb-4">
              Guests are financially responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Any damage beyond normal wear and tear</li>
              <li>Missing items from the property</li>
              <li>Excessive cleaning required due to guest actions</li>
              <li>Damage caused by unauthorized guests or pets</li>
            </ul>
            <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
              <p className="text-slate-700 text-sm">
                Hosts may charge for damages through the platform. Disputed charges will be 
                reviewed by SWFL Rentals support.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Prohibited Activities
            </h2>
            <p className="text-slate-600 mb-4">The following are strictly prohibited:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Illegal activities of any kind</li>
              <li>Commercial events or parties without Host permission</li>
              <li>Exceeding the maximum occupancy</li>
              <li>Subletting or transferring your booking</li>
              <li>Recording devices in private areas</li>
              <li>Harassment of Hosts, neighbors, or other guests</li>
              <li>Violating HOA or community rules</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Check-in & Check-out
            </h2>
            <p className="text-slate-600 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Arrive during the designated check-in time</li>
              <li>Contact the Host if you'll be significantly delayed</li>
              <li>Depart by the stated check-out time</li>
              <li>Return all keys and access devices</li>
              <li>Follow checkout procedures specified by the Host</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Communication
            </h2>
            <p className="text-slate-600 mb-4">Good communication includes:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Responding promptly to Host messages</li>
              <li>Using the platform messaging system for records</li>
              <li>Communicating any issues during your stay</li>
              <li>Being respectful in all communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Reviews
            </h2>
            <p className="text-slate-600 mb-4">
              When leaving reviews:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Be honest and accurate in your feedback</li>
              <li>Focus on your actual experience</li>
              <li>Do not leave reviews as retaliation or leverage</li>
              <li>Do not include personal information about Hosts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              10. Enforcement
            </h2>
            <p className="text-slate-600 mb-4">
              Violations of these responsibilities may result in:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Warning notices</li>
              <li>Booking cancellation without refund</li>
              <li>Charges for damages or violations</li>
              <li>Account suspension or termination</li>
              <li>Legal action in severe cases</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              11. Contact
            </h2>
            <p className="text-slate-600">
              For questions about guest responsibilities, please contact us at:<br />
              Email: support@southwestflvacationrentals.com
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
